/**
 * Shared LeadLoop views — the badges, timestamps, and record cards that make a
 * Lead, a Call, and a Form Entry read as three parts of one system.
 *
 * WHY THIS FILE EXISTS. Cascade's LeadLoop was polished one detail at a time
 * and every piece of that polish lived inside `apps/admin-cascade-web`: the
 * lifecycle badge that renders nothing for Active, the Verified checkmark that
 * is deliberately not a badge, the exact-plus-relative timestamp, the queue
 * card built on `RecordSummaryCard`. One Up's LeadLoop had grown its own
 * near-copies of all four, already drifting — a different card padding, a
 * different set of pills, an Airtable status map that had to be deleted once
 * before because two copies of a vocabulary is how two portals start
 * disagreeing about what a stored value means.
 *
 * So the presentation moved here, beside the form-submission and voice-call
 * views that already serve both portals. Cascade re-exports these from its
 * `views/parts.tsx` so its imports are untouched and there is still exactly one
 * implementation; One Up imports them directly.
 *
 * WHAT IS NOT HERE. Anything that needs an API client, a router, or a portal's
 * error type. These take plain DTOs and callbacks. The Action Centers, the
 * intake decision flows, and the detail panes stay in their portals, because
 * they differ in what they are allowed to write.
 */

import type { ReactNode } from 'react';
import { ArrowRight, Building2, CalendarClock, Check, Sparkles, User } from 'lucide-react';
import {
  Badge,
  RecordMeta,
  RecordSummaryCard,
  cn,
  type RecordTypeId,
} from '@command-center/ui-system';
import {
  cascadeStatusLabel,
  cascadeStatusTone,
  type CascadeLeadStatusTone,
} from '@command-center/leadloop-core';

/* -------------------------------------------------------------- timestamps */

/** "Aug 14 · 3:42 PM". For inside a record, where the year is already implied. */
export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return value;
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The unambiguous stamp: "Aug 14, 2026 · 3:42 PM".
 *
 * `formatDateTime` drops the year, which is fine inside a record that already
 * sits in a dated context but not on a queue card — "13h ago" and "Aug 14" both
 * leave a caller wondering which year an old entry arrived in, and somebody
 * reading a feed of inquiries needs to be able to say the exact time back to the
 * person who called. Rendered in the browser's own locale and timezone.
 */
export function formatExactDateTime(value?: string | null): string | null {
  if (!value) return null;
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return value;
  const date = new Date(ts);
  const day = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
}

/** "13h ago". Kept beside the exact stamp, never instead of it. */
export function formatRelativeTime(value?: string | null): string | null {
  if (!value) return null;
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return null;
  const minutes = Math.max(0, Math.floor((Date.now() - ts) / 60_000));
  if (minutes < 60) return `${Math.max(1, minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * When something arrived, both ways round.
 *
 * The exact stamp leads because it is the fact; the relative reading trails it
 * in muted text because it is the shortcut. Showing only the shortcut is what
 * made the queue unusable for calling someone back.
 */
export function TimeStamp({ value, className }: { value?: string | null; className?: string }) {
  const exact = formatExactDateTime(value);
  if (!exact) return null;
  const relative = formatRelativeTime(value);
  return (
    <span className={cn('inline-flex flex-wrap items-baseline gap-x-1.5', className)}>
      <span className="whitespace-nowrap tabular-nums text-foreground">{exact}</span>
      {relative && <span className="whitespace-nowrap text-muted-foreground">({relative})</span>}
    </span>
  );
}

/* ------------------------------------------------------------------ badges */

const STATUS_TONE_CLASS: Record<CascadeLeadStatusTone, string> = {
  new: 'bg-info/15 text-info',
  active: '',
  verified_qualified: 'bg-success/20 text-success',
  dismissed: 'bg-muted text-muted-foreground',
};

/**
 * The one lead lifecycle badge.
 *
 * Takes a raw status — canonical, display label, or a retired legacy value —
 * and renders at most one badge. Colour comes from leadloop-core's tone map, so
 * the queue, the Leads list, the cards, the table, and the detail pane can never
 * disagree about what "Verified & Qualified" looks like, in either portal.
 *
 * `active` renders NOTHING, and that is the point. Active is the ordinary state
 * of a lead somebody is working: it is true of almost every card in the queue,
 * so a badge saying it costs a slot in the corner of every card and tells the
 * reader something they already assumed. What earns a badge is a departure from
 * the ordinary — untouched, finished, or abandoned.
 */
export function LeadStatusBadge({
  status,
  /**
   * Say "Active" out loud anyway.
   *
   * For a table, where a column header has promised a value and a blank cell
   * reads as missing data rather than as "nothing worth saying". It renders as
   * muted text, not a badge, so a column of Active leads stays quiet.
   */
  showActive = false,
}: {
  status: string;
  showActive?: boolean;
}) {
  const tone = cascadeStatusTone(status);
  if (tone === 'active') {
    if (!showActive) return null;
    return <span className="text-[12px] text-muted-foreground">Active</span>;
  }
  return (
    <Badge className={cn('border-transparent', STATUS_TONE_CLASS[tone])}>
      {cascadeStatusLabel(status)}
    </Badge>
  );
}

/**
 * Verified, as the quiet attribute it is.
 *
 * Deliberately not a filled Badge: verification is a fact about the lead, not a
 * state in its lifecycle, and giving it the same visual weight as New or
 * Verified & Qualified would put two different kinds of claim side by side and
 * invite the reader to treat "Verified" as a stage.
 */
export function VerifiedTag({ verified }: { verified?: boolean }) {
  if (!verified) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
      <Check className="h-3 w-3" aria-hidden />
      Verified
    </span>
  );
}

/**
 * Where a Call or Form Entry stands.
 *
 * New is the only unresolved state that gets a badge. An unresolved item that
 * somebody has already written a note on is still work, still in the queue, and
 * still shows its actions — but "Open" or "Reviewed" would be a word the reader
 * has to learn in order to be told something the notes already show them.
 */
const INTAKE_LIFECYCLE_BADGE: Record<string, { label: string; className: string } | null> = {
  new: { label: 'New', className: 'bg-info/15 text-info' },
  open: null,
  resolved: { label: 'Resolved', className: 'bg-success/20 text-success' },
  dismissed: { label: 'Dismissed', className: 'bg-muted text-muted-foreground' },
};

export function IntakeStateBadge({
  lifecycle,
  resolutionLabel,
}: {
  lifecycle?: string;
  /** "Lead Created" / "Linked to Lead" — the decision, not the category. */
  resolutionLabel?: string;
}) {
  const badge = lifecycle ? INTAKE_LIFECYCLE_BADGE[lifecycle] : undefined;
  if (!badge) return null;
  return (
    <Badge className={cn('border-transparent', badge.className)}>
      {resolutionLabel ?? badge.label}
    </Badge>
  );
}

const LOOP_REASON_LABELS: Record<string, string> = {
  approval_pending: 'Approval pending',
  overdue: 'Overdue',
  due_now: 'Due',
  new_lead: 'New lead',
  needs_next_step: 'Needs next step',
  scheduled: 'Scheduled',
};

/**
 * Reasons that earn a badge.
 *
 * Only three, and two of them are the derived attention state: a follow-up date
 * that has arrived (`due_now`) or passed (`overdue`). Neither is stored — they
 * are read off `next_follow_up_at` every time the feed is built, which is why
 * Due can never disagree with the date on the record.
 *
 * Three reasons deliberately render nothing. `new_lead` duplicates the New
 * lifecycle badge, which is on the same card. `needs_next_step` and `scheduled`
 * describe which bucket the reader is already looking at; labelling every card
 * in a tab with the name of that tab is noise.
 */
const INFORMATIVE_REASONS = new Set(['overdue', 'due_now', 'approval_pending']);

export function isInformativeReason(reason: string): boolean {
  return INFORMATIVE_REASONS.has(reason);
}

export function ReasonBadge({ reason }: { reason: string }) {
  const urgent = reason === 'overdue';
  return (
    <Badge variant={urgent ? 'destructive' : 'warning'}>
      {LOOP_REASON_LABELS[reason] ?? reason}
    </Badge>
  );
}

/** Renders the queue reason only when it adds information beyond the status. */
export function LoopReasonBadge({ reason }: { reason?: string }) {
  if (!reason || !isInformativeReason(reason)) return null;
  return <ReasonBadge reason={reason} />;
}

/**
 * The plain-language "what should I do with this" line for a queue reason.
 *
 * One copy, because it is read in three places — the queue card, the quick
 * view, and the Action Center on the record — and three copies had already
 * started to drift. Deliberately says what to do, never what stage this is:
 * LeadLoop does not know whether the next touch is a quote, a site visit, or a
 * text message, and it must not pretend to.
 */
export function loopActionLabel(reason?: string): string {
  switch (reason) {
    case 'new_lead':
      return 'Review and make first contact';
    case 'overdue':
      return 'Complete the overdue follow-up';
    case 'due_now':
      return 'Follow up today';
    case 'approval_pending':
      return 'Review the proposed action';
    case 'scheduled':
      return 'Scheduled — nothing needed yet';
    default:
      return 'Set a follow-up, or finish this lead';
  }
}

/**
 * Where a lead came from, in words.
 *
 * Stored sources are snake_case (`website_form`, `phone_call`) because the
 * vocabulary is closed; nobody should read that.
 */
export function formatSourceLabel(source?: string | null): string | undefined {
  if (!source) return undefined;
  return source
    .split('_')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/* ------------------------------------------------------------------- leads */

/**
 * The fields a Lead card needs, and nothing more.
 *
 * Structural on purpose: Cascade's `LccLead` and One Up's `LeadLoopLead` are
 * separately declared DTOs of the same Airtable row, and this satisfies both
 * rather than forcing either portal to adopt the other's type.
 */
export interface LeadLoopLeadView {
  id: string;
  title: string;
  status: string;
  contactName?: string | null;
  companyName?: string | null;
  email?: string | null;
  phone?: string | null;
  message?: string | null;
  aiSummary?: string | null;
  source?: string | null;
  sourceLabel?: string | null;
  assignedTo?: string | null;
  verified?: boolean;
  nextStep?: string | null;
  nextFollowUpAt?: string | null;
  createdAt?: string | null;
}

/**
 * One Lead in a feed.
 *
 * Two badges at most, and each answers a different question: where is this in
 * its life (New / Verified & Qualified / Dismissed — nothing at all when it is
 * simply Active), and does it want me today (Due / Overdue). Verified rides
 * quietly beside them as the attribute it is.
 *
 * The card is for DECIDING which lead to work, not for working it. Notes, the
 * follow-up date, Verified, and the two terminal decisions all live in one
 * Action Center inside the record — copies of them on every card would bury the
 * feed under the same form twenty times over. `actions` and `primaryAction` are
 * there for the one control a portal legitimately wants on the card face.
 */
export function LeadLoopLeadCard({
  lead,
  reason,
  dueAt,
  selected,
  onOpen,
  actions,
  primaryAction,
  extraBadges,
}: {
  lead: LeadLoopLeadView;
  /** Loop reason, when the portal builds a loop feed. Drives the flag + hint. */
  reason?: string;
  dueAt?: string | null;
  selected?: boolean;
  onOpen: () => void;
  actions?: ReactNode;
  primaryAction?: ReactNode;
  extraBadges?: ReactNode;
}) {
  const attention = Boolean(reason) && reason !== 'scheduled';
  return (
    <RecordSummaryCard
      recordType="lead"
      title={lead.title}
      onOpen={onOpen}
      selected={selected}
      // A lead the queue is actually asking about, versus one that is merely
      // still open. Only the first should pull the eye across the grid.
      flagged={attention}
      badges={
        <>
          <LeadStatusBadge status={lead.status} />
          <LoopReasonBadge reason={reason} />
          <VerifiedTag verified={lead.verified} />
          {extraBadges}
        </>
      }
      phone={lead.phone}
      email={lead.email}
      meta={
        <>
          <RecordMeta icon={User}>{lead.contactName}</RecordMeta>
          <RecordMeta icon={Building2}>{lead.companyName}</RecordMeta>
          <RecordMeta>{lead.sourceLabel ?? formatSourceLabel(lead.source)}</RecordMeta>
          <TimeStamp value={lead.createdAt} />
        </>
      }
      summary={lead.aiSummary ?? lead.message}
      highlight={
        <>
          {/*
            The action line is for a lead that wants something. A `scheduled`
            lead does not — it is resting until a date that has not arrived — and
            "Scheduled — nothing needed yet" set in the accent colour behind an
            arrow reads as an instruction, which is the opposite of what it says.
            Same rule the reason BADGE follows: scheduled is silent, and the
            follow-up date below carries the only fact worth having.
          */}
          {attention && (
            <span className="inline-flex items-center gap-1.5 font-medium text-primary">
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              {loopActionLabel(reason)}
            </span>
          )}
          {lead.nextStep && (
            <span className="text-muted-foreground">
              Next step: <span className="text-foreground">{lead.nextStep}</span>
            </span>
          )}
          {(dueAt ?? lead.nextFollowUpAt) && (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <CalendarClock className="h-3.5 w-3.5" aria-hidden />
              {formatDateTime(dueAt ?? lead.nextFollowUpAt)}
            </span>
          )}
        </>
      }
      actions={actions}
      primaryAction={primaryAction}
    />
  );
}

/** Condensed row for the picker list beside an open Lead. */
export function LeadLoopLeadListItem({
  lead,
  reason,
  selected,
  onSelect,
  extraBadges,
}: {
  lead: LeadLoopLeadView;
  reason?: string;
  selected?: boolean;
  onSelect: () => void;
  extraBadges?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full flex-col gap-1 rounded-md px-2.5 py-2 text-left transition-colors',
        selected ? 'bg-selection text-foreground' : 'text-foreground hover:bg-muted/70',
      )}
    >
      <span className="truncate text-[13px] font-medium">{lead.title}</span>
      <span className="truncate text-[11px] text-muted-foreground">
        {lead.contactName ?? lead.companyName ?? 'No contact'}
      </span>
      <span className="flex flex-wrap items-center gap-1.5">
        <LeadStatusBadge status={lead.status} />
        <LoopReasonBadge reason={reason} />
        <VerifiedTag verified={lead.verified} />
        {extraBadges}
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ intake */

/**
 * One unreviewed Call or Form Entry, as the queue sees it.
 *
 * Again structural: this is the wire shape both portals already serve from
 * `intakeItemToDto`.
 */
export interface LeadLoopIntakeView {
  id: string;
  sourceType: string;
  lifecycle?: string;
  resolutionLabel?: string;
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  summary?: string | null;
  aiRecommendation?: string | null;
  /** Normalized 0–1 confidence supplied by the existing intake analysis. */
  aiConfidence?: number | null;
  occurredAt?: string | null;
}

export function intakeConfidencePercent(value?: number | null): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.round(Math.max(0, Math.min(1, value)) * 100);
}

export function intakeRecordType(sourceType: string): RecordTypeId {
  return sourceType === 'call' ? 'call' : sourceType === 'time_entry' ? 'time_entry' : 'form_entry';
}

export function intakeDisplayName(item: LeadLoopIntakeView): string {
  return (
    item.displayName?.trim()
    || (item.sourceType === 'call'
      ? 'Unknown caller'
      : item.sourceType === 'time_entry' ? 'Unnamed Time Entry' : 'Unnamed submission')
  );
}

/**
 * The intake card.
 *
 * Drawn as a PROPOSAL rather than a record — dashed border — because there is
 * no business record behind it yet. The type strip still leads, so a mixed
 * queue of Calls and Form Entries is scannable without reading a single field.
 *
 * Install-specific callers choose which decisions to place in the footer. The
 * buttons still open the shared review dialog so the classification and matches
 * remain visible before anything is committed.
 */
export function LeadLoopIntakeCard({
  item,
  selected,
  onOpen,
  actions,
  primaryAction,
}: {
  item: LeadLoopIntakeView;
  selected?: boolean;
  onOpen: () => void;
  actions?: ReactNode;
  primaryAction?: ReactNode;
}) {
  const confidence = intakeConfidencePercent(item.aiConfidence);
  return (
    <RecordSummaryCard
      recordType={intakeRecordType(item.sourceType)}
      title={intakeDisplayName(item)}
      onOpen={onOpen}
      selected={selected}
      provisional
      flagged={item.lifecycle === 'new'}
      badges={
        <>
          <IntakeStateBadge lifecycle={item.lifecycle} resolutionLabel={item.resolutionLabel} />
          {confidence !== null && <Badge variant="outline">AI {confidence}%</Badge>}
        </>
      }
      phone={item.phone}
      email={item.email}
      meta={
        <>
          <RecordMeta icon={Building2}>{item.company}</RecordMeta>
          <TimeStamp value={item.occurredAt} />
        </>
      }
      summary={item.summary}
      highlight={
        item.aiRecommendation ? (
          <span className="inline-flex items-start gap-1.5 text-muted-foreground">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            {item.aiRecommendation}
          </span>
        ) : undefined
      }
      actions={actions}
      primaryAction={primaryAction}
    />
  );
}

/** Condensed row for the picker list beside an open intake item. */
export function LeadLoopIntakeListItem({
  item,
  selected,
  onSelect,
}: {
  item: LeadLoopIntakeView;
  selected?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full flex-col gap-1 rounded-md px-2.5 py-2 text-left transition-colors',
        selected ? 'bg-selection text-foreground' : 'text-foreground hover:bg-muted/70',
      )}
    >
      <span className="truncate text-[13px] font-medium">{intakeDisplayName(item)}</span>
      <span className="truncate text-[11px] text-muted-foreground">
        {[item.sourceType === 'call' ? 'Call' : item.sourceType === 'time_entry' ? 'Time Entry' : 'Form Entry', formatDateTime(item.occurredAt)]
          .filter(Boolean)
          .join(' · ')}
      </span>
      <span className="flex flex-wrap items-center gap-1.5">
        <IntakeStateBadge lifecycle={item.lifecycle} resolutionLabel={item.resolutionLabel} />
      </span>
    </button>
  );
}
